import React from 'react';
import { Grid, Card } from '@mui/material';
import MetricDisplay from 'components/atoms/MetricDisplay';

/**
 * Default tooltips for common metrics
 */
const METRIC_TOOLTIPS = {
  annual_return: (
    <>
      <p>Rate of Return Calculations</p>
      <p>To comply with NFA regulations, we display Cumulative Rate of Return for strategies 
      with a track record of less than one year. For strategies with longer track records, 
      we display Annualized (Compounded) Rate of Return.</p>
      <p>How Annualized (Compounded) Rate of Return is calculated:</p>
      <p>= ((Ending_equity / Starting_equity) ^ (1 / age_in_years)) - 1</p>
      <p>Remember that, following NFA requirements, strategy subscription costs and 
      estimated commissions are included in marked-to-market equity calculations.</p>
      <p>All results are hypothetical.</p>
    </>
  ),
  max_drawdown: (
    <>
      <p>The largest peak-to-valley historical drawdown of the Model Account. 
      Future drawdowns may be greater than this number.</p>
      <p>This number assumes typical broker commissions and fees are included in 
      strategy performance.</p>
    </>
  ),
  category: 'The investment strategy category, which describes its general trading approach.',
  win_months: 'The number of months where the strategy produced a positive return (in percentage terms).',
  num_trades: 'The total number of trades executed by the strategy during its entire history.',
  profit_percent: 'The percentage of trades that were profitable out of all trades executed by the strategy.',
};

/**
 * Definition of metric configurations
 * Each metric has a key, label, and display options
 */
const METRICS_CONFIG = {
  annual_return: {
    label: 'Annual Return (Compounded)',
    metricType: 'percentage',
    tooltip: METRIC_TOOLTIPS.annual_return,
    colorByValue: true
  },
  drawdown: {
    label: 'Max Drawdown',
    metricType: 'percentage',
    tooltip: METRIC_TOOLTIPS.max_drawdown,
    color: 'error.main'
  },
  num_trades: {
    label: 'Num Trades',
    metricType: 'default',
    color: 'light.main',
    tooltip: METRIC_TOOLTIPS.num_trades
  },
  profit_percent: {
    label: '% Profitable',
    metricType: 'percentage',
    color: 'light.main',
    tooltip: METRIC_TOOLTIPS.profit_percent
  },
  category: {
    label: 'Category',
    metricType: 'default',
    tooltip: METRIC_TOOLTIPS.category
  },
  win_months: {
    label: 'Win Months',
    metricType: 'winLoss',
    tooltip: METRIC_TOOLTIPS.win_months
  },
  today_change: {
    label: "Today's Change",
    metricType: 'percentage',
    colorByValue: true
  },
  total_change: {
    label: 'All Time Change',
    metricType: 'percentage',
    colorByValue: true
  },
  return_30d: {
    label: '30 Day Return',
    metricType: 'percentage',
    colorByValue: true
  },
  return_60d: {
    label: '60 Day Return',
    metricType: 'percentage',
    colorByValue: true
  },
  return_90d: {
    label: '90 Day Return',
    metricType: 'percentage',
    colorByValue: true
  }
};

/**
 * StrategyStatistics - A component for displaying a collection of strategy metrics
 * 
 * @param {Object} props
 * @param {Object} props.strategy - The strategy object with metrics
 * @param {Array} [props.metrics] - Array of metric keys to display (defaults to standard set)
 * @param {string} [props.size] - Size variant ('small', 'medium', 'large')
 * @param {boolean} [props.loading] - Whether metrics are loading
 * @param {Object} [props.tradeStats] - Additional trade statistics if not from metrics
 * @returns {JSX.Element}
 */
const StrategyStatistics = ({ 
  strategy, 
  metrics = ['annual_return', 'drawdown', 'num_trades', 'profit_percent', 'category', 'win_months'],
  size = 'medium',
  loading = false,
  tradeStats = {}
}) => {
  const getMetricValue = (metricKey) => {
    // Handle specific metrics that need custom data extraction
    switch(metricKey) {
      case 'annual_return':
        return strategy?.performance_metrics?.annual_return;
      case 'drawdown':
        return strategy?.performance_metrics?.drawdown?.percentage;
      case 'num_trades':
        return strategy?.performance_metrics?.num_trades;
      case 'profit_percent': 
        return strategy?.performance_metrics?.profit_percent;
      case 'category':
        return strategy?.category_display || strategy?.category;
      case 'win_months':
        return strategy?.performance_metrics?.win_loss_months;
      case 'today_change':
        return strategy?.performance_metrics?.value_change_today?.percentage;
      case 'total_change':
        return strategy?.performance_metrics?.value_change_total?.percentage;
      case 'return_30d':
        return strategy?.performance_metrics?.return_30d?.percentage;
      case 'return_60d':
        return strategy?.performance_metrics?.return_60d?.percentage;
      case 'return_90d':
        return strategy?.performance_metrics?.return_90d?.percentage;
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={2}>
      {metrics.map((metricKey) => {
        const config = METRICS_CONFIG[metricKey] || { label: metricKey };
        const value = getMetricValue(metricKey);
        
        return (
          <Grid item xs={6} sm={4} md={2} key={metricKey}>
            <Card sx={{ height: '100%' }}>
              <MetricDisplay
                value={value}
                label={config.label}
                metricType={config.metricType}
                tooltip={config.tooltip}
                color={config.color}
                colorByValue={config.colorByValue}
                loading={loading}
                size={size}
              />
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default StrategyStatistics; 